import React from 'react';
import { styled } from '@mui/material';
import TextField, {
    TextFieldProps,
} from 'components/interface/textField/TextField';
import Symbol from 'components/Symbol';
import Calendar from './Calendar';
import { CalendarPopover } from './CalendarPopover';
import { DateRange, DateRangePickerVariant } from './types';
import { formatPeriodValue } from './utils';
import { parseCalendarRange } from './dateOnly';

const EndAdornment = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const Anchor = styled('div', {
    shouldForwardProp: prop => prop !== '$fullWidth',
})<{
    $fullWidth?: boolean;
}>(({ $fullWidth }) => ({
    display: $fullWidth ? 'block' : 'inline-block',
    width: $fullWidth ? '100%' : 'auto',
}));

export interface DateRangePickerProps extends Omit<
    TextFieldProps,
    'value' | 'onChange' | 'variant'
> {
    period: DateRange | null;
    onChange: (period: DateRange | null) => void;
    disableBefore?: string;
    disableAfter?: string;
    closeOnPick?: boolean;
    variant?: DateRangePickerVariant;
}

export const DateRangePicker = ({
    period,
    onChange,
    disableBefore,
    disableAfter,
    closeOnPick = true,
    variant = 'range',
    disabled,
    onBaseClick,
    onKeyDown,
    placeholder = 'Välj period',
    variantSx,
    inputSx,
    endAdornment,
    ...textFieldProps
}: DateRangePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);

    const normalizedPeriod = React.useMemo(
        () => (period ? parseCalendarRange(period) : null),
        [period]
    );

    const displayValue = React.useMemo(
        () => formatPeriodValue(normalizedPeriod),
        [normalizedPeriod]
    );

    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);

    const handleOpen = React.useCallback(() => {
        if (!disabled) {
            setOpen(true);
        }
    }, [disabled]);

    const handleToggle = React.useCallback(() => {
        if (!disabled) {
            if (open) {
                handleClose();
                return;
            }

            handleOpen();
        }
    }, [disabled, handleClose, handleOpen, open]);

    return (
        <Anchor ref={setAnchorEl} $fullWidth={textFieldProps.fullWidth}>
            <TextField
                {...textFieldProps}
                value={displayValue}
                onBaseClick={event => {
                    onBaseClick?.(event);
                    handleToggle();
                }}
                onKeyDown={event => {
                    onKeyDown?.(event);

                    if (event.key === 'Enter' || event.key === ' ') {
                        handleToggle();
                        event.preventDefault();
                        return;
                    }

                    if (event.key === 'ArrowDown') {
                        handleOpen();
                        event.preventDefault();
                    }

                    if (event.key === 'Escape') {
                        handleClose();
                    }
                }}
                readOnly
                disabled={disabled}
                placeholder={placeholder}
                endAdornment={
                    endAdornment !== undefined ? (
                        endAdornment
                    ) : (
                        <EndAdornment>
                            <Symbol
                                symbol='date_range'
                                size='sm'
                                color='textSecondary'
                            />
                            <Symbol
                                symbol={
                                    open
                                        ? 'keyboard_arrow_up'
                                        : 'keyboard_arrow_down'
                                }
                                size='sm'
                                color='textSecondary'
                            />
                        </EndAdornment>
                    )
                }
                variantSx={{
                    ...variantSx,
                    cursor: disabled ? 'default' : 'pointer',
                }}
                inputSx={{
                    ...inputSx,
                    cursor: disabled ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            />
            <CalendarPopover
                open={open}
                anchorEl={anchorEl}
                transition
                placement='bottom'
                PaperProps={{
                    elevation: 8,
                }}
                onClose={handleClose}
                hideOverflow>
                <Calendar
                    periods={period ? [period] : []}
                    variant={variant}
                    onChange={periods => {
                        const nextPeriod = periods[0] || null;
                        onChange(nextPeriod);
                    }}
                    onSelectionComplete={() => {
                        if (closeOnPick) {
                            handleClose();
                        }
                    }}
                    disableBefore={disableBefore}
                    disableAfter={disableAfter}
                />
            </CalendarPopover>
        </Anchor>
    );
};

export default DateRangePicker;
